import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Briefcase, Users, Cpu, MoreVertical, Trash2, X, Edit2, User as UserIcon, Check, AlignLeft, Shield, Mail, BadgeCheck, Folder, LayoutGrid } from 'lucide-react';
import { Workspace, User, Agent } from '../types';
import { useAuth } from '../../context/AuthContext';
import { fetchWorkflows } from '../../adk-agent-builder/api';
import { Zap } from 'lucide-react';

interface WorkspacesPageProps {
  workspaces: Workspace[];
  users: User[];
  agents: Agent[];
  onOpenWorkspace: (ws: Workspace) => void;
  onAddWorkspace: (ws: Workspace) => void;
  onUpdateWorkspace: (ws: Workspace) => void;
  onDeleteWorkspace: (id: string) => void;
}

const WorkspacesPage: React.FC<WorkspacesPageProps> = ({
  workspaces,
  users,
  agents,
  onOpenWorkspace,
  onAddWorkspace,
  onUpdateWorkspace,
  onDeleteWorkspace
}) => {
  const [showModal, setShowModal] = useState<'new' | 'edit' | 'members' | null>(null);
  const [editingWs, setEditingWs] = useState<Workspace | null>(null);
  const [wsName, setWsName] = useState('');
  const [wsDesc, setWsDesc] = useState('');
  const [wsOwner, setWsOwner] = useState('');

  // Set default owner when modal opens or users change
  useEffect(() => {
    if (showModal === 'new' && users.length > 0 && !wsOwner) {
      setWsOwner(users[0].id); // Default to first user's ID
    }
  }, [showModal, users, wsOwner]);

  const [selectedMembers, setSelectedMembers] = useState<{ userId: string; role: 'Viewer' | 'Admin' | 'Builder' }[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  const [newUserOpen, setNewUserOpen] = useState(false);
  const [newMemberId, setNewMemberId] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'Viewer' | 'Admin' | 'Builder'>('Viewer');

  const [newAgentOpen, setNewAgentOpen] = useState(false);
  const [newAgentId, setNewAgentId] = useState('');

  const [savedWorkflows, setSavedWorkflows] = useState<{ id: string, name: string }[]>([]);

  useEffect(() => {
    fetchWorkflows().then(setSavedWorkflows).catch(() => { });
  }, []);

  const { user } = useAuth();
  const normalizedRole = (user?.role || '').toLowerCase().trim();
  const isBuilderRole = normalizedRole === 'builder';

  const canManageWorkspace = (ws: Workspace) => {
    if (normalizedRole === 'viewer' || isBuilderRole) return false;

    // If the user's explicit role in this workspace is Viewer or Builder, they cannot manage it.
    const explicitWorkspaceMember = ws.members?.find(m => m.userId === user?.id);
    if (explicitWorkspaceMember?.role === 'Viewer') return false;
    if (explicitWorkspaceMember?.role === 'Builder') return false;

    // Check if the user has an admin role at the org level or is Super Admin
    if (user?.role === 'Super Admin' || user?.role === 'Org Admin') return true;

    // If they are a Workspace Admin globally without org context (mostly a backend thing now, but kept for safety)
    if (user?.role === 'Workspace Admin') return true;

    // Finally, check if they are an Admin on this specific workspace
    return ws.members?.some(m => m.userId === user?.id && m.role === 'Admin') || false;
  };

  const canEditWorkspace = (ws: Workspace) => {
    if (normalizedRole === 'viewer') return false;

    // Builder users should always be able to edit workspace details.
    if (isBuilderRole) return true;

    // Explicit workspace viewer gets no edit rights.
    const explicitWorkspaceMember = ws.members?.find(m => m.userId === user?.id);
    if (explicitWorkspaceMember?.role === 'Viewer') return false;

    if (canManageWorkspace(ws)) return true;

    // Compatibility: allow edit when explicit workspace role is Builder
    // for non-Builder org roles.
    if (explicitWorkspaceMember?.role === 'Builder') return true;

    return false;
  };

  const resetForm = () => {
    setWsName('');
    setWsDesc('');
    setWsOwner(users[0]?.id || ''); // Default to first user's ID
    setSelectedMembers([]);
    setSelectedAgentIds([]);
    setEditingWs(null);
    setShowModal(null);
    setNewUserOpen(false);
    setNewMemberId('');
    setNewMemberRole('Viewer');
    setNewAgentOpen(false);
    setNewAgentId('');
  };

  const handleCreate = () => {
    if (!wsName.trim()) return;
    if (newUserOpen && newMemberId) {
      alert("Please click 'Add' to add the selected member first, or close the member selection.");
      return;
    }
    if (newAgentOpen && newAgentId) {
      alert("Please click 'Add' to add the selected workflow first, or close the workflow selection.");
      return;
    }

    const newWs: Workspace = {
      id: Date.now().toString(), // Backend will ignore/replace this
      name: wsName,
      description: wsDesc,
      owner: wsOwner, // This is now the user ID
      status: 'Active',
      members: selectedMembers.map(m => ({
        userId: m.userId,
        role: m.role,
        workspaceId: '',
        joinedAt: new Date().toISOString()
      })),
      agentIds: selectedAgentIds,
    };

    onAddWorkspace(newWs);
    resetForm();
  };

  const handleUpdate = () => {
    if (!editingWs || !wsName.trim()) return;
    if (newUserOpen && newMemberId) {
      alert("Please click 'Add' to add the selected member first, or close the member selection.");
      return;
    }
    if (newAgentOpen && newAgentId) {
      alert("Please click 'Add' to add the selected workflow first, or close the workflow selection.");
      return;
    }

    const updatedWs: Workspace = {
      ...editingWs,
      name: wsName,
      description: wsDesc,
      owner: wsOwner, // This is now the user ID
      status: editingWs.status,
      members: selectedMembers.map(m => ({
        userId: m.userId,
        role: m.role,
        workspaceId: editingWs.id,
        joinedAt: new Date().toISOString()
      })),
      agentIds: selectedAgentIds,
    };

    onUpdateWorkspace(updatedWs);
    resetForm();
  };

  const openEditModal = (ws: Workspace, mode: 'edit' | 'members' = 'edit') => {
    setEditingWs(ws);
    setWsName(ws.name);
    setWsDesc(ws.description);
    setWsOwner(ws.owner); // Directly set the ID

    // Map existing members to selection state, migrating any legacy Editors to Viewer
    const validMembers: { userId: string; role: 'Viewer' | 'Admin' | 'Builder' }[] = (ws.members || []).map(m => ({
      userId: m.userId,
      role: (m.role === 'Admin' ? 'Admin' : m.role === 'Builder' ? 'Builder' : 'Viewer') as 'Viewer' | 'Admin' | 'Builder'
    }));

    // Compatibility: If members empty but userIds exist (from old data), try to migrate
    if (validMembers.length === 0 && ws.userIds && ws.userIds.length > 0) {
      ws.userIds.forEach(uid => {
        if (users.some(u => u.id === uid)) {
          validMembers.push({ userId: uid, role: 'Viewer' });
        }
      });
    }

    const validAgentIds = ws.agentIds || [];

    setSelectedMembers(validMembers);
    setSelectedAgentIds(validAgentIds);
    setShowModal(mode);
    setActiveMenuId(null);

    // Auto open new user input if in 'members' mode
    if (mode === 'members') {
      setNewUserOpen(true);
    }
  };

  const addMember = () => {
    if (!newMemberId) return;
    if (selectedMembers.some(m => m.userId === newMemberId)) return;

    setSelectedMembers([...selectedMembers, { userId: newMemberId, role: newMemberRole }]);
    setNewMemberId('');
    setNewMemberRole('Viewer');
    setNewUserOpen(false);
  };

  const removeMember = (userId: string) => {
    setSelectedMembers(selectedMembers.filter(m => m.userId !== userId));
  };

  const updateMemberRole = (userId: string, role: 'Viewer' | 'Admin' | 'Builder') => {
    setSelectedMembers(selectedMembers.map(m => m.userId === userId ? { ...m, role } : m));
  };

  const toggleAgent = (id: string) => {
    setSelectedAgentIds(prev =>
      prev.includes(id) ? prev.filter(aid => aid !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Workspaces</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Manage organizational units and project environments.</p>
        </div>
        <button
          onClick={() => setShowModal('new')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
        >
          <Plus size={18} /> New Workspace
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
        {workspaces.map((ws) => {
          const isViewer = !canManageWorkspace(ws) && !canEditWorkspace(ws);
          const canSeeWorkspaceMenu = !isViewer || isBuilderRole;
          return (
            <div key={ws.id} className="bg-white dark:bg-slate-900 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 p-6 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
              {/* Folder Icon & Title Section */}
              <div className="flex items-start gap-3 mb-5">
                <div className="p-3 bg-blue-50/50 dark:bg-blue-900/20 rounded-xl text-blue-600 dark:text-blue-400 shadow-sm">
                  <Folder size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 transition-colors uppercase tracking-tight">{ws.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1 mt-0.5 font-medium">{ws.description}</p>
                </div>
                {canSeeWorkspaceMenu && (
                  <div className="relative">
                    <button
                      onClick={() => setActiveMenuId(activeMenuId === ws.id ? null : ws.id)}
                      className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-300 transition-colors"
                    >
                      <MoreVertical size={18} />
                    </button>
                    {activeMenuId === ws.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-2xl z-20 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                        <button
                          onClick={() => onOpenWorkspace(ws)}
                          className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 font-bold"
                        >
                          <LayoutGrid size={16} className="text-blue-500" /> Open App
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Large Stat Blocks (The "SaturnIQ" look) */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="p-4 bg-indigo-50/40 dark:bg-indigo-900/10 rounded-2xl border border-indigo-100/50 dark:border-indigo-900/20 flex flex-col items-center justify-center text-center group/stat hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors">
                  <span className="text-2xl font-black text-indigo-600 dark:text-indigo-400 leading-none mb-1">
                    {ws.members?.length || ws.userCount || 0}
                  </span>
                  <span className="text-[9px] font-black text-indigo-400 dark:text-indigo-500 uppercase tracking-widest group-hover/stat:text-indigo-500 transition-colors">Members</span>
                </div>

                <div className="p-4 bg-purple-50/40 dark:bg-purple-900/10 rounded-2xl border border-purple-100/50 dark:border-purple-900/20 flex flex-col items-center justify-center text-center group/org hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors">
                  <span className="text-[11px] font-black text-purple-700 dark:text-purple-400 leading-tight mb-0.5 line-clamp-1">{ws.organization_name || 'EngazeIQ Platform'}</span>
                  <span className="text-[9px] font-black text-purple-400 dark:text-purple-500 uppercase tracking-widest group-hover/org:text-purple-500 transition-colors">ORG</span>
                </div>
              </div>

              {/* Action buttons list */}
              <div className="flex flex-wrap items-center gap-2 mb-5">
                {canEditWorkspace(ws) && (
                  <button
                    onClick={() => openEditModal(ws, 'edit')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:text-blue-600 dark:hover:text-blue-400 transition-all border border-transparent hover:border-blue-100 dark:hover:border-blue-800"
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                )}

                {canManageWorkspace(ws) && !isBuilderRole && (
                  <button
                    onClick={() => openEditModal(ws, 'members')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-indigo-50 dark:hover:bg-indigo-900/40 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all border border-transparent hover:border-indigo-100 dark:hover:border-indigo-800"
                  >
                    <Users size={12} /> Manage Members
                  </button>
                )}

                {canManageWorkspace(ws) && (
                  <button
                    onClick={() => onDeleteWorkspace(ws.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-lg text-[10px] font-black uppercase tracking-wider hover:bg-red-50 dark:hover:bg-red-900/40 hover:text-red-600 dark:hover:text-red-400 transition-all border border-transparent hover:border-red-100 dark:hover:border-red-800 ml-auto"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>

              {/* Footer with Timestamp */}
              <div className="pt-3 border-t border-slate-50 dark:border-slate-800 flex items-center justify-between text-slate-400 dark:text-slate-500">
                <span className="text-[9px] font-bold tracking-tight">Created {ws.created_at || '2/16/2026'}</span>
                <div className={`w-1.5 h-1.5 rounded-full shadow-sm animate-pulse ${ws.status === 'Active' ? 'bg-emerald-400' : 'bg-slate-300 dark:bg-slate-700'}`}></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal for New/Edit Workspace */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4 sm:p-6">
          <div className={`bg-white dark:bg-slate-900 rounded-[2rem] w-full ${showModal === 'members' ? 'max-w-xl' : 'max-w-3xl'} shadow-2xl animate-in fade-in zoom-in duration-300 overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800 transition-all`}>
            {/* Header with Gradient Bar */}
            <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 to-indigo-600"></div>

            <div className="px-8 py-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600 dark:from-slate-100 dark:to-slate-400">
                  {showModal === 'new' ? 'Create New Workspace' : showModal === 'members' ? 'Manage Team' : 'Edit Workspace'}
                </h2>
                <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                  {showModal === 'members' ? `Manage members for ${wsName}` : 'Define your team environment and member permissions.'}
                </p>
              </div>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-slate-100 rounded-2xl text-slate-400 hover:text-slate-600 transition-all hover:rotate-90 duration-300"
              >
                <X size={22} />
              </button>
            </div>

            <div className={`p-8 grid gap-8 overflow-y-auto ${showModal === 'members' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
              {/* Left Column: Basic Info - Hide if in 'members' mode */}
              {showModal !== 'members' && (
                <div className="space-y-6">
                  <div>
                    <label className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">
                      <Briefcase size={12} className="text-blue-500" />
                      Workspace Name
                    </label>
                    <input
                      type="text"
                      value={wsName}
                      onChange={(e) => setWsName(e.target.value)}
                      placeholder="e.g. Sales Department"
                      className="w-full px-5 py-3 bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-medium focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all placeholder:text-slate-400 text-slate-900 dark:text-slate-100"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">
                      <AlignLeft size={12} className="text-blue-500" />
                      Description
                    </label>
                    <textarea
                      value={wsDesc}
                      onChange={(e) => setWsDesc(e.target.value)}
                      placeholder="Briefly describe what this workspace is for..."
                      rows={4}
                      className="w-full px-5 py-3 bg-slate-50/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-medium focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none text-slate-900 dark:text-slate-100"
                    />
                  </div>


                </div>
              )}

              {/* Right Column: User Management & Agents */}
              <div className="flex flex-col h-full gap-6">
                {/* Team Members - only shown in New Workspace or Manage Team, NOT in Edit */}
                {showModal !== 'edit' && (
                  <div className="flex-1 flex flex-col">
                    <div className="flex justify-between items-end mb-3">
                      <label className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em]">
                        <Users size={12} className="text-blue-500" />
                        Team Members
                      </label>
                      <button
                        onClick={() => setNewUserOpen(!newUserOpen)}
                        className={`text-xs font-bold flex items-center gap-1.5 px-3 py-1 rounded-full transition-all ${newUserOpen ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                          }`}
                      >
                        {newUserOpen ? <X size={12} /> : <Plus size={12} />}
                        {newUserOpen ? 'Close' : 'Add Member'}
                      </button>
                    </div>

                    {/* Compact Add Member Form */}
                    {newUserOpen && (
                      <div className="mb-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50/30 border border-blue-100 rounded-2xl space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-1 gap-3">
                          <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                              <Mail size={14} />
                            </div>
                            <select
                              value={newMemberId}
                              onChange={(e) => setNewMemberId(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-blue-100 dark:border-blue-900/40 focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                            >
                              <option value="" className="dark:bg-slate-900">Select teammate...</option>
                              {users
                                .filter(u => !selectedMembers.some(m => m.userId === u.id))
                                .map(user => (
                                  <option key={user.id} value={user.id} className="dark:bg-slate-900">{user.name}</option>
                                ))}
                            </select>
                          </div>

                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400">
                                <BadgeCheck size={14} />
                              </div>
                              <select
                                value={newMemberRole}
                                onChange={(e) => setNewMemberRole(e.target.value as any)}
                                className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white font-medium"
                                disabled={user?.role === 'Workspace Admin'}
                              >
                                <option value="Viewer">Workspace Viewer</option>
                                <option value="Builder">Workspace Builder</option>
                                {user?.role !== 'Workspace Admin' && (
                                  <>
                                    <option value="Admin">Workspace Admin</option>
                                  </>
                                )}
                              </select>
                            </div>
                            <button
                              onClick={addMember}
                              disabled={!newMemberId}
                              className="px-6 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 shadow-md shadow-blue-500/20 transition-all active:scale-95"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Members List Scrolling Area */}
                    <div className="flex-1 min-h-[220px] bg-slate-50/50 border border-slate-200 rounded-[1.5rem] overflow-hidden flex flex-col">
                      {selectedMembers.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-slate-300 mb-3 shadow-sm">
                            <Users size={24} />
                          </div>
                          <p className="text-sm font-bold text-slate-400">No members yet</p>
                          <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">Start by adding your team</p>
                        </div>
                      ) : (
                        <div className="overflow-y-auto max-h-[300px] divide-y divide-slate-100">
                          {selectedMembers.map(member => {
                            const user = users.find(u => u.id === member.userId);
                            if (!user) return null;
                            return (
                              <div
                                key={member.userId}
                                className="flex items-center justify-between px-5 py-3 hover:bg-white transition-colors group"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center font-bold text-white shadow-sm shadow-blue-500/20 group-hover:scale-105 transition-transform">
                                    {user.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">{user.name}</span>
                                    <span className="text-xs text-slate-400 leading-tight">{user.email}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <select
                                    value={member.role}
                                    onChange={(e) => updateMemberRole(member.userId, e.target.value as 'Viewer' | 'Admin' | 'Builder')}
                                    className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border shadow-sm outline-none cursor-pointer ${member.role === 'Admin'
                                      ? 'bg-purple-50 text-purple-600 border-purple-100'
                                      : member.role === 'Builder'
                                        ? 'bg-amber-50 text-amber-600 border-amber-100'
                                        : 'bg-slate-50 text-slate-500 border-slate-100'
                                      }`}
                                  >
                                    <option value="Admin">WORKSPACE ADMIN</option>
                                    <option value="Builder">WORKSPACE BUILDER</option>
                                    <option value="Viewer">WORKSPACE VIEWER</option>
                                  </select>
                                  <button
                                    onClick={() => removeMember(member.userId)}
                                    className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                    title="Remove Member"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Agents Section */}
                {showModal !== 'members' && (
                  <div className="flex-1 flex flex-col max-h-[220px]">
                    <div className="flex justify-between items-end mb-3">
                      <label className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em]">
                        <Zap size={12} className="text-blue-500" />
                        Agents & Workflows
                      </label>
                      <button
                        onClick={() => setNewAgentOpen(!newAgentOpen)}
                        className={`text-xs font-bold flex items-center gap-1.5 px-3 py-1 rounded-full transition-all ${newAgentOpen ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' : 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                          }`}
                      >
                        {newAgentOpen ? <X size={12} /> : <Plus size={12} />}
                        {newAgentOpen ? 'Close' : 'Add Workflow'}
                      </button>
                    </div>

                    {/* Compact Add Agent/Workflow Form */}
                    {newAgentOpen && (
                      <div className="mb-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50/30 border border-amber-100 rounded-2xl space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="flex gap-2">
                          <div className="flex-1 relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500">
                              <Cpu size={14} />
                            </div>
                            <select
                              value={newAgentId}
                              onChange={(e) => setNewAgentId(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-amber-100 dark:border-amber-900/40 focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium"
                            >
                              <option value="" className="dark:bg-slate-900">Select workflow or agent...</option>
                              {savedWorkflows.filter(w => !selectedAgentIds.includes(w.id)).map(w => (
                                <option key={w.id} value={w.id} className="dark:bg-slate-900">Workflow: {w.name.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          </div>
                          <button
                            onClick={() => {
                              if (newAgentId && !selectedAgentIds.includes(newAgentId)) {
                                setSelectedAgentIds([...selectedAgentIds, newAgentId]);
                                setNewAgentId('');
                              }
                            }}
                            disabled={!newAgentId}
                            className="px-6 py-2 bg-amber-500 text-white text-sm font-bold rounded-xl hover:bg-amber-600 disabled:opacity-50 shadow-md shadow-amber-500/20 transition-all active:scale-95"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex-1 min-h-[140px] bg-slate-50/50 border border-slate-200 rounded-[1.5rem] overflow-hidden flex flex-col">
                      {selectedAgentIds.length === 0 ? (
                        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center">
                          <p className="text-sm font-bold text-slate-400">No agents added</p>
                          <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">Add from Agents page</p>
                        </div>
                      ) : (
                        <div className="overflow-y-auto max-h-[200px] divide-y divide-slate-100">
                          {selectedAgentIds.map(agentId => {
                            const wflow = savedWorkflows.find(w => w.id === agentId);
                            const mockAgent = agents.find(a => a.id === agentId);
                            const name = wflow ? wflow.name.replace(/_/g, ' ') : (mockAgent ? mockAgent.name : agentId);
                            return (
                              <div
                                key={agentId}
                                className="flex items-center justify-between px-5 py-3 hover:bg-white transition-colors group"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                                    <Cpu size={16} />
                                  </div>
                                  <span className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">{name}</span>
                                </div>
                                <button
                                  onClick={() => setSelectedAgentIds(selectedAgentIds.filter(id => id !== agentId))}
                                  className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                  title="Remove Agent"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Actions Footer */}
            <div className="px-8 py-6 bg-slate-50/80 dark:bg-slate-800/80 backdrop-blur-sm border-t border-slate-100 dark:border-slate-800 flex gap-4 flex-shrink-0">
              <button
                onClick={resetForm}
                className="flex-1 px-6 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-all active:scale-[0.98]"
              >
                Cancel
              </button>
              <button
                onClick={showModal === 'new' ? handleCreate : handleUpdate}
                disabled={!wsName.trim()}
                className="flex-[1.5] px-6 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl text-sm font-bold hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all active:scale-[0.98]"
              >
                {showModal === 'new' ? 'Create Workspace' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WorkspacesPage;
